import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { conversationContextSummary, conversationRecordSchema, type ConversationRecord } from "@opencloud/agent-protocol";

export type AgentFeedEvent = {
  id: number;
  conversationId: string;
  appId: string | null;
  type: "agent.message.created" | "agent.run.status" | "agent.runtime.display" | "agent.continuity.status" | "agent.workspace.updated";
  data: Record<string, unknown>;
};

export type AgentConversationActivity = {
  lastUserMessageAt: string | null;
  lastAgentMessageAt: string | null;
};

type ConversationRemovedEvent = {
  type: "conversation.removed";
  conversationId: string;
  appId: string | null;
};

export type AgentEventStream = {
  connected: boolean;
  stale: boolean;
  ready: boolean;
  conversations: readonly ConversationRecord[];
  streamedConversationIds: ReadonlySet<string>;
  workingConversationIds: ReadonlySet<string>;
  attentionConversationIds: ReadonlySet<string>;
  workingAppIds: ReadonlySet<string>;
  attentionAppIds: ReadonlySet<string>;
  latestMessages: ReadonlyMap<string, string>;
  conversationActivity: ReadonlyMap<string, AgentConversationActivity>;
  upsertConversation(conversation: ConversationRecord): void;
  removeConversation(conversationId: string): void;
  refresh(): void;
  subscribe(listener: (event: AgentFeedEvent | ConversationRemovedEvent) => void): () => void;
};

type Listener = Parameters<AgentEventStream["subscribe"]>[0];

export function useAgentEvents(enabled: boolean, viewerUserId?: string | null): AgentEventStream {
  // Read through a ref so a session refresh never replaces the live connection.
  const viewer = useRef<string | null>(viewerUserId ?? null);
  viewer.current = viewerUserId ?? null;
  const [connected, setConnected] = useState(false);
  const [stale, setStale] = useState(false);
  const [ready, setReady] = useState(false);
  const [conversations, setConversations] = useState<ConversationRecord[]>([]);
  const [streamedConversationIds, setStreamedConversationIds] = useState<Set<string>>(new Set());
  const [workingConversationIds, setWorkingConversationIds] = useState<Set<string>>(new Set());
  const [attentionConversationIds, setAttentionConversationIds] = useState<Set<string>>(new Set());
  const [latestMessages, setLatestMessages] = useState<Map<string, string>>(new Map());
  const [conversationActivity, setConversationActivity] = useState<Map<string, AgentConversationActivity>>(new Map());
  const [generation, setGeneration] = useState(0);
  const failedReconnects = useRef(0);
  const listeners = useRef(new Set<Listener>());
  const conversationIds = useRef(new Set<string>());
  const subscribe = useCallback((listener: Listener) => {
    listeners.current.add(listener);
    return () => { listeners.current.delete(listener); };
  }, []);
  const upsertConversation = useCallback((conversation: ConversationRecord) => {
    conversationIds.current.add(conversation.id);
    setConversations((current) => {
      const retained = current.filter((record) => record.id !== conversation.id);
      return [conversation, ...retained].sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
    });
    setConversationActivity((current) => current.has(conversation.id) ? current
      : new Map(current).set(conversation.id, {
        lastUserMessageAt: conversation.updatedAt,
        lastAgentMessageAt: null,
      }));
  }, []);
  const removeConversation = useCallback((conversationId: string) => {
    conversationIds.current.delete(conversationId);
    setConversations(current => current.filter(record => record.id !== conversationId));
    setStreamedConversationIds(current => without(current, conversationId));
    setWorkingConversationIds(current => without(current, conversationId));
    setAttentionConversationIds(current => without(current, conversationId));
    setLatestMessages(current => withoutMap(current, conversationId));
    setConversationActivity(current => withoutMap(current, conversationId));
  }, []);
  const refresh = useCallback(() => setGeneration((current) => current + 1), []);

  useEffect(() => {
    if (!enabled) {
      setConnected(false);
      setStale(false);
      setReady(false);
      conversationIds.current.clear();
      setConversations([]);
      setStreamedConversationIds(new Set());
      setWorkingConversationIds(new Set());
      setAttentionConversationIds(new Set());
      setLatestMessages(new Map());
      setConversationActivity(new Map());
      return;
    }
    if (typeof EventSource === "undefined") return;
    const source = new EventSource("/v1/events");
    let active = true;
    let reconnect: number | undefined;
    source.onopen = () => {
      if (!active) return;
      failedReconnects.current = 0;
      setConnected(true); setStale(false);
    };
    source.onerror = () => {
      if (!active) return;
      setConnected(false); setStale(true);
      // The browser reopens a dropped stream by itself, but stops for good when
      // a reopen is answered with an error status, such as a 503 while sign-in
      // checks are slow. Open a new stream after a growing pause instead.
      if (source.readyState !== EventSource.CLOSED || reconnect !== undefined) return;
      const delay = Math.min(30_000, 1_000 * 2 ** failedReconnects.current);
      failedReconnects.current += 1;
      reconnect = window.setTimeout(() => setGeneration((current) => current + 1), delay);
    };
    source.addEventListener("directory", (raw) => {
      if (!active) return;
      try {
        const value = JSON.parse((raw as MessageEvent).data) as Record<string, unknown>;
        if (!Array.isArray(value.conversations)) return;
        const records = value.conversations.map((record) => conversationRecordSchema.parse(record));
        const incomingIds = new Set(records.map((record) => record.id));
        const ids = new Set([...conversationIds.current, ...incomingIds]);
        conversationIds.current = ids;
        const working = new Set(readIds(value.workingConversationIds).filter((id) => incomingIds.has(id)));
        const attention = new Set(readIds(value.attentionConversationIds).filter((id) => incomingIds.has(id)));
        const activity = readActivity(value.conversationActivity, incomingIds);
        setConversations((current) => {
          const byId = new Map(current.map((record) => [record.id, record]));
          for (const record of records) byId.set(record.id, record);
          return [...byId.values()];
        });
        setStreamedConversationIds((current) => new Set([...current, ...incomingIds]));
        setWorkingConversationIds((current) => new Set([...working,
          ...[...current].filter((id) => !incomingIds.has(id))]));
        setAttentionConversationIds((current) => new Set([...attention,
          ...[...current].filter((id) => !incomingIds.has(id))]));
        setConversationActivity((current) => {
          const next = new Map(current);
          for (const [id, summary] of activity) next.set(id, summary.activity);
          return next;
        });
        setLatestMessages((current) => {
          const next = new Map(current);
          for (const [id, summary] of activity) {
            if (summary.latestMessage) next.set(id, summary.latestMessage);
          }
          return next;
        });
        setReady(true);
      } catch { /* A later directory refresh or JSON snapshot recovers malformed input. */ }
    });
    source.addEventListener("conversation.removed", (raw) => {
      if (!active) return;
      try {
        const value = JSON.parse((raw as MessageEvent).data) as Record<string, unknown>;
        if (typeof value.conversationId !== "string") return;
        const conversationId = value.conversationId;
        const appId = typeof value.appId === "string" ? value.appId : null;
        conversationIds.current.delete(conversationId);
        setConversations((current) => current.filter((record) => record.id !== conversationId));
        setStreamedConversationIds((current) => without(current, conversationId));
        setWorkingConversationIds((current) => without(current, conversationId));
        setAttentionConversationIds((current) => without(current, conversationId));
        setLatestMessages((current) => withoutMap(current, conversationId));
        setConversationActivity((current) => withoutMap(current, conversationId));
        for (const listener of listeners.current) listener({ type: "conversation.removed", conversationId, appId });
      } catch { /* Ignore malformed removal notices. */ }
    });
    for (const type of ["agent.message.created", "agent.run.status", "agent.runtime.display", "agent.continuity.status", "agent.workspace.updated"] as const) {
      source.addEventListener(type, (raw) => {
        if (!active) return;
        try {
          const message = raw as MessageEvent;
          const value = JSON.parse(message.data) as Record<string, unknown>;
          if (typeof value.conversationId !== "string" || !record(value.event)) return;
          const event: AgentFeedEvent = {
            id: Number(message.lastEventId) || 0,
            conversationId: value.conversationId,
            appId: typeof value.appId === "string" ? value.appId : null,
            type,
            data: type === "agent.continuity.status" || type === "agent.workspace.updated" ? {} : value.event,
          };
          applySummaryEvent(event, setConversations, setWorkingConversationIds,
            setAttentionConversationIds, setLatestMessages, setConversationActivity, viewer.current);
          for (const listener of listeners.current) listener(event);
        } catch { /* Durable snapshots recover malformed or unsupported feed entries. */ }
      });
    }
    source.addEventListener("access_denied", () => {
      if (!active) return;
      source.close();
      setConnected(false);
      setStale(true);
      setReady(false);
      const removed = [...conversationIds.current];
      conversationIds.current.clear();
      setConversations([]);
      setStreamedConversationIds(new Set());
      setWorkingConversationIds(new Set());
      setAttentionConversationIds(new Set());
      setLatestMessages(new Map());
      setConversationActivity(new Map());
      for (const conversationId of removed) {
        for (const listener of listeners.current) listener({ type: "conversation.removed", conversationId, appId: null });
      }
    });
    return () => {
      active = false;
      if (reconnect !== undefined) clearTimeout(reconnect);
      source.close(); setConnected(false);
    };
  }, [enabled, generation]);

  const appActivity = useMemo(() => {
    const appByConversation = new Map(conversations.map((record) => [record.id, record.appId]));
    return {
      working: new Set([...workingConversationIds].flatMap((id) => appByConversation.get(id) ?? [])),
      attention: new Set([...attentionConversationIds].flatMap((id) => appByConversation.get(id) ?? [])),
    };
  }, [attentionConversationIds, conversations, workingConversationIds]);

  return useMemo(() => ({ connected, stale, ready, conversations, streamedConversationIds,
    workingConversationIds, attentionConversationIds,
    workingAppIds: appActivity.working, attentionAppIds: appActivity.attention,
    latestMessages, conversationActivity, upsertConversation, removeConversation, refresh, subscribe }),
  [appActivity, attentionConversationIds, connected, conversationActivity, conversations, latestMessages, ready,
    stale, streamedConversationIds, subscribe, upsertConversation, removeConversation, refresh, workingConversationIds]);
}

function applySummaryEvent(
  event: AgentFeedEvent,
  setConversations: Dispatch<SetStateAction<ConversationRecord[]>>,
  setWorking: Dispatch<SetStateAction<Set<string>>>,
  setAttention: Dispatch<SetStateAction<Set<string>>>,
  setLatestMessages: Dispatch<SetStateAction<Map<string, string>>>,
  setConversationActivity: Dispatch<SetStateAction<Map<string, AgentConversationActivity>>>,
  viewerUserId: string | null = null,
): void {
  if (event.type === "agent.continuity.status") return;
  const occurredAt = typeof event.data.occurredAt === "string" ? event.data.occurredAt : null;
  if (occurredAt) {
    setConversations((current) => current.map((record) => record.id === event.conversationId
      ? { ...record, updatedAt: occurredAt }
      : record));
  }
  if (event.type === "agent.message.created") {
    const message = record(event.data.message) ? event.data.message : null;
    // A summary the agent continued from is not a reply, so it never becomes
    // the chat preview or marks the chat unread.
    if (message && message.presentation === "conversation" && typeof message.content === "string" &&
        conversationContextSummary({ role: String(message.role), content: message.content }) === null) {
      const content = message.content;
      setLatestMessages((current) => new Map(current).set(event.conversationId, content));
      if (typeof message.createdAt === "string") {
        const createdAt = message.createdAt;
        // The server already counts a person's own outside change as read.
        const ownAppEvent = record(message.appEvent) && record(message.actor) &&
          viewerUserId !== null && message.actor.userId === viewerUserId;
        setConversations((current) => current.map((item) => item.id === event.conversationId
          ? { ...item, updatedAt: createdAt, unread: ownAppEvent ? item.unread : true }
          : item));
        if (message.role === "user" || message.role === "agent") {
          setConversationActivity((current) => {
            const previous = current.get(event.conversationId) ?? {
              lastUserMessageAt: null,
              lastAgentMessageAt: null,
            };
            return new Map(current).set(event.conversationId, message.role === "user"
              ? { ...previous, lastUserMessageAt: createdAt }
              : { ...previous, lastAgentMessageAt: createdAt });
          });
        }
      }
    }
    return;
  }
  if (event.type !== "agent.run.status" || event.data.parentRunId) return;
  const status = typeof event.data.status === "string" ? event.data.status : "";
  const phase = typeof event.data.phase === "string" ? event.data.phase : "";
  const working = phase !== "finished" && !["idle", "completed", "failed", "cancelled"].includes(status);
  const attention = ["failed", "waiting_for_user"].includes(status);
  setWorking((current) => working ? withValue(current, event.conversationId) : without(current, event.conversationId));
  setAttention((current) => attention ? withValue(current, event.conversationId) : without(current, event.conversationId));
}

function readActivity(
  value: unknown,
  conversationIds: ReadonlySet<string>,
): Map<string, { activity: AgentConversationActivity; latestMessage: string | null }> {
  const result = new Map<string, { activity: AgentConversationActivity; latestMessage: string | null }>();
  if (!Array.isArray(value)) return result;
  for (const item of value) {
    if (!record(item) || typeof item.conversationId !== "string" || !conversationIds.has(item.conversationId)) continue;
    result.set(item.conversationId, {
      activity: {
        lastUserMessageAt: typeof item.lastUserMessageAt === "string" ? item.lastUserMessageAt : null,
        lastAgentMessageAt: typeof item.lastAgentMessageAt === "string" ? item.lastAgentMessageAt : null,
      },
      latestMessage: typeof item.latestMessage === "string" ? item.latestMessage : null,
    });
  }
  return result;
}

function readIds(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function record(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function withValue(current: Set<string>, value: string): Set<string> {
  if (current.has(value)) return current;
  const next = new Set(current); next.add(value); return next;
}

function without(current: Set<string>, value: string): Set<string> {
  if (!current.has(value)) return current;
  const next = new Set(current); next.delete(value); return next;
}

function withoutMap<T>(current: Map<string, T>, value: string): Map<string, T> {
  if (!current.has(value)) return current;
  const next = new Map(current); next.delete(value); return next;
}
